import {
  useMutation,
  useQuery,
  useQueryClient,
  UseQueryOptions,
} from "@tanstack/react-query";
import { get, post } from "../services/apiClient";

export interface SubscriptionAmountSummary {
  count: number;
  amount: number;
}

export interface SubscriptionStats {
  total: number;
  active: number;
  trialing: number;
  cancelled: number;
  expired: number;
  totalRevenue: number;
  monthRevenue: number;
  transactionCount: number;
  pendingCollection: SubscriptionAmountSummary;
  collected: SubscriptionAmountSummary;
}

export const useSubscriptionStats = () =>
  useQuery<SubscriptionStats>({
    queryKey: ["subscriptions", "stats"],
    queryFn: () => get<SubscriptionStats>("/admin/subscriptions/stats"),
    retry: false,
  });
import { ownerKeys } from "./useOwners";

export type SubscriptionStatus =
  | "TRIALING"
  | "ACTIVE"
  | "PAST_DUE"
  | "INCOMPLETE"
  | "PAUSED"
  | "GRACE_PERIOD"
  | "CANCELLED"
  | "EXPIRED"
  | "SUSPENDED";

export type Gateway = "RAZORPAY" | "STRIPE" | "MANUAL";

export interface SubscriptionRow {
  id: string;
  ownerId: string;
  planId?: string | null;
  gateway: Gateway | string;
  status: SubscriptionStatus | string;
  billingCycle?: "MONTHLY" | "YEARLY" | "CUSTOM" | string;
  isCustom?: boolean | null;
  customDurationDays?: number | null;
  currency?: string;
  unitAmount?: number | string | null;
  startedAt?: string | null;
  currentPeriodStart: string;
  currentPeriodEnd: string;
  trialStartAt?: string | null;
  trialEndAt?: string | null;
  gracePeriodEnd?: string | null;
  cancelAtPeriodEnd?: boolean;
  cancelledAt?: string | null;
  createdAt: string;
  currentMonthCollected?: boolean;
  owner?: {
    id: string;
    name: string;
    phone: string;
    email?: string | null;
  } | null;
  plan?: {
    id: string;
    code?: string;
    name: string;
    priceMonthly: number | string;
  } | null;
}

export interface SubscriptionListParams {
  page?: number;
  limit?: number;
  status?: SubscriptionStatus;
  ownerId?: string;
  gateway?: Gateway;
}

interface SubscriptionListResponse {
  data: SubscriptionRow[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export const useSubscriptionList = (
  params: SubscriptionListParams = {},
  options?: Omit<
    UseQueryOptions<SubscriptionListResponse>,
    "queryKey" | "queryFn"
  >,
) =>
  useQuery<SubscriptionListResponse>({
    queryKey: ["subscriptions", "list", params],
    queryFn: () =>
      get<SubscriptionListResponse>("/admin/subscriptions", params),
    retry: false,
    ...options,
  });

export const useCancelSubscription = (ownerId?: string) => {
  const qc = useQueryClient();
  return useMutation<unknown, Error, { id: string; reason?: string }>({
    mutationFn: ({ id, reason }) =>
      post(`/admin/subscriptions/${id}/cancel`, { reason }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["subscriptions"] });
      if (ownerId)
        qc.invalidateQueries({ queryKey: ownerKeys.subscription(ownerId) });
    },
  });
};

export const useReactivateSubscription = (ownerId?: string) => {
  const qc = useQueryClient();
  return useMutation<unknown, Error, { id: string }>({
    mutationFn: ({ id }) => post(`/admin/subscriptions/${id}/reactivate`, {}),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["subscriptions"] });
      if (ownerId)
        qc.invalidateQueries({ queryKey: ownerKeys.subscription(ownerId) });
    },
  });
};

export const useAssignPlan = (ownerId?: string) => {
  const qc = useQueryClient();
  return useMutation<
    unknown,
    Error,
    { ownerId: string; planId: string; billingCycle?: string }
  >({
    mutationFn: (body) => post("/admin/subscriptions/assign", body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["subscriptions"] });
      if (ownerId)
        qc.invalidateQueries({ queryKey: ownerKeys.subscription(ownerId) });
    },
  });
};

export const useCollectMonth = () => {
  const qc = useQueryClient();
  return useMutation<unknown, Error, { id: string; month: string }>({
    mutationFn: ({ id, month }) =>
      post(`/admin/subscriptions/${id}/collect-month`, { month }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["subscriptions"] });
      qc.invalidateQueries({ queryKey: ["transactions"] });
    },
  });
};

export const useAssignCustomSubscription = (ownerId?: string) => {
  const qc = useQueryClient();
  return useMutation<
    unknown,
    Error,
    {
      ownerId: string;
      amount: number;
      durationDays: number;
      planId?: string;
      notes?: string;
      paymentStatus?: string;
    }
  >({
    mutationFn: (body) => post("/admin/subscriptions/custom", body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["subscriptions"] });
      qc.invalidateQueries({ queryKey: ["invoices"] });
      if (ownerId)
        qc.invalidateQueries({ queryKey: ownerKeys.subscription(ownerId) });
    },
  });
};

export const useExtendSubscription = (ownerId?: string) => {
  const qc = useQueryClient();
  return useMutation<unknown, Error, { id: string; additionalDays: number }>({
    mutationFn: ({ id, additionalDays }) =>
      post(`/admin/subscriptions/${id}/extend`, { additionalDays }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["subscriptions"] });
      if (ownerId)
        qc.invalidateQueries({ queryKey: ownerKeys.subscription(ownerId) });
    },
  });
};
